import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { AppNodes } from "@models/enums/dvm/DvmNodes.enum";
import { GroupAssignedAmenity } from "@models/interfaces/amenities/AmenityGroupAssigned.interface";
import { Menu, MenuContent, MenuContentItem } from "@models/interfaces/components/Menu.interface";
import { Filter } from "@models/types/app/Filter.type";
import { AmenitiesService } from "@services/amenities.service";
import { DvmService } from "@services/dvm.service";
import { FilterService } from "@services/filter.service";
import { LayoutService } from "@services/layout.service";
import { BottomSheetService } from "@services/components/bottom-sheet.service";
import { MinimapService } from "@services/components/minimap.service";
import { findMenuContentById } from "./menu-content-tree.util";

@Injectable()
export class MenuSelectionService {

    // SERVICES
    private readonly ams:           AmenitiesService        = inject(AmenitiesService);
    private readonly dvm:           DvmService              = inject(DvmService);
    private readonly filters:       FilterService           = inject(FilterService);
    private readonly layout:        LayoutService           = inject(LayoutService);
    private readonly bottomSheet:   BottomSheetService      = inject(BottomSheetService);
    private readonly minimap:       MinimapService          = inject(MinimapService);

    // STATE
    public  readonly itemSelected:  WritableSignal<MenuContentItem | null> = signal<MenuContentItem|null>(null);

    // PUBLIC METHODS

    /**
     * Método que establece el elemento del menú seleccionado.
     * 
     * Este método se utiliza para seleccionar un elemento del menú y actualizar la disponibilidad de los asientos relacionados.
     * Si el elemento seleccionado es el mismo que el actual, se deselecciona.
     * 
     * @param menuItem - Elemento del menú a seleccionar.
     */
    public setMenuItem(menuItem: MenuContent | null): void {

        // Si el menu es AMS, seteamos el amenity seleccionado en el servicio AMS.
        if(menuItem?.amsId && this.ams.amsMap.get(menuItem.amsId)) {

            this.ams.setAmenityFromMenu(this.ams.amsMap.get(menuItem.amsId) as GroupAssignedAmenity);

        }
        // Si no es AMS, pero hay un amenity seleccionado, lo reseteamos.
        else if(this.ams.selectedAmenities().length) {
            
            this.ams.resetSelectedAmenities();

        }
        
        // Instanciamos menu data como MenuContentItem ( o null si no hay menuItem )
        const menu: MenuContentItem | null = menuItem ? menuItem.data : null;

        if(menu){

            // Si el menu seleccionado es el mismo que el actual, lo deseleccionamos, si no, establecemos el nuevo menú seleccionado.
            this.itemSelected.set(this.itemSelected()?.id === menu.id ? null : menu);

        }
        else{

            // Si el menu es null, deseleccionamos el item seleccionado.
            this.itemSelected.set(null);

        }
        
        // Deseleccionamos cualquier selección previa
        this.dvm.viewer.unselectAll()

        // Limpiamos los filtros
        this.filters.clearFilters();

        // Si hay elemento seleccionado
        if ( this.itemSelected() ){

            // Seteamos los filtros
            for(const [key, value] of Object.entries(this.itemSelected()!.relatedSeats || {}))
            this.filters.setFilters(key as keyof Filter, value);

            // Set availability for each node type on the map.
            // For each type, only the relatedSeats.section IDs that belong to that type remain available.
            // This supports maps with non-standard types (area, areaprice, etc.) in addition to section.
            const sectionIds: string[] = this.filters.filter().section;
            this.dvm.viewer.getTypesList().forEach((type: string) => {
                if (type === AppNodes.Ga) return;
                const idsOfType: string[] = this.dvm.viewer.getNodesByType(type).map(n => n.id!);
                const available: string[] = sectionIds.filter(id => idsOfType.includes(id));
                this.dvm.viewer.setAvailability(type, available);
            });

            // Forzamos la selección de las secciones
            this.dvm.viewer.select(this.filters.filter().section);

        }

        // Si estamos deseleccionando
        else {
            
            this.dvm.setAvailability();

        }
        
        // Si estamos en mobile y el bottom sheet está abierto, lo cerramos.
        if(this.layout.isMobile() && this.bottomSheet.state() !== 'closed') this.bottomSheet.close();

        // Hacemos que el viewer vaya a la selección o regrese su vista original.
        // Filtramos los IDs de sección que existan en el mapa para evitar errores de "Invalid destination".
        const sectionIds:        string[] = this.itemSelected()?.relatedSeats?.section ?? [];
        const validDestination:  string[] = sectionIds.filter(id => !!this.dvm.viewer.getNodeById(id));
        this.dvm.goTo(
            validDestination.length ? validDestination : this.dvm.viewer.limits.bbox_scene
        );

    }

    /**
     * Método que selecciona un elemento del menú.
     * 
     * Si el elemento del menú tiene la propiedad is3dView a true y tiene un viewId, se carga la vista 3D correspondiente.
     * Si no, se establece el elemento del menú como el elemento seleccionado.
     * @param item - Elemento del menú a seleccionar.
     */
    public selectItem(item: MenuContent | null): void {

        if (item?.data.is3dView && item.data.viewId) {
            this.openMenu3dView(item.data.viewId);
        } else {
            this.setMenuItem(item);
        }

    }

    /**
     * Método que limpia la selección actual del menú, filtros y amenities.
     * 
     */
    public clearCurrentSelection(): void {

        // Limpiamos el item seleccionado
        if(this.itemSelected()) this.itemSelected.set(null);

        // Si es mobil y el bottom sheet está abierto, lo cerramos.
        if(this.layout.isMobile() && this.bottomSheet.state() !== 'closed') this.bottomSheet.close();

        // Limpiamos seleccion DVM
        this.dvm.viewer.unselectAll()
        
        // Limpiamos filtros
        this.filters.clearFilters();

        // Reseteamos amenities seleccionados
        this.ams.resetSelectedAmenities();
        
    }

    /**
     * Método que comprueba si el menú seleccionado tiene el elemento seleccionado.
     * 
     * Este método se utiliza para comprobar si el menú seleccionado tiene el elemento seleccionado.
     * Si no hay un menú seleccionado o no hay un elemento seleccionado, devuelve false.
     * @param menu Menu en el que se busca el item seleccionado.
     * @returns true si el menú seleccionado tiene el elemento seleccionado, false en caso contrario.
     */
    public hasMenuSelectedItem(menu?: Menu): boolean {

        const selected: MenuContentItem | null = this.itemSelected();
    
        if (!menu || !selected) return false;

        return !!findMenuContentById(menu.content, selected.id);

    }

    /**
     * Método que activa el hover sobre un elemento del menú.
     * 
     * Este método se utiliza para activar el hover sobre un elemento del menú y resaltar los asientos relacionados.
     * Si no se pasa un elemento del menú, se desactiva el hover.
     * 
     * @param menuItem - Elemento del menú sobre el que hacer hover.
     */
    public hoverMenuItem(menuItem?: MenuContentItem): void {

        if(!menuItem || !menuItem.relatedSeats?.section?.length) return; 

        this.dvm.viewer.hover(menuItem.relatedSeats?.section)

    }

    /**
     * Método que desactiva el hover sobre el menú.
     * 
     * Este método se utiliza para desactivar el hover sobre el menú y eliminar los asientos resaltados.
     * 
     */
    public leaveMenuItem(): void {

        this.dvm.viewer.hover([]);

    }

    // PRIVATE METHODS

    /**
     * Método que abre una vista 3D del DVM.
     * 
     * Este método se utiliza para cargar una vista 3D del DVM cuando se selecciona un elemento del menú que tiene la propiedad is3dView a true.
     * @param viewId - ID de la vista 3D a cargar.
     */
    private openMenu3dView(viewId: string): void {        

        this.dvm.load3dView(viewId).subscribe({
          next: (): void => {
            this.clearCurrentSelection();
            this.minimap.setVisibility(false);
          },
          error: (): void => undefined
        });

    }

}
